import React, { useEffect, useState, useRef } from 'react';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { searchAppBooking, acceptBooking } from '../../constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';
import AppTable from '../../components/AppTable/AppTable';
import { setPagination } from '../../redux/Actions/TableAction';
import CustomModal from '../../components/CustomModal/CustumModal';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CheckCircle, XCircle, Clock, Calendar, MapPin, Eye, ShoppingCart, RotateCcw, UserCheck, RefreshCw, Bell, Volume2, Users } from 'lucide-react';
import AssignStaffModal from './AssignStaffModal';
import RescheduleModal from './RescheduleModal';
import TableSearch from '../../components/TableSearch/TableSearch';
import DateSearch from '../../components/DateSearch/DateSearch';
import { getElevateUser } from '../../storage/Storage';

function AppBookingMaster() {
  const dispatch = useDispatch();
  const ApiReducer = useSelector((state) => state.ApiReducer);
  const TableDataReducer = useSelector((state) => state.TableReducer);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [optionSelect, setOptionSelect] = useState('');
  const [valueSearched, setValueSearched] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [refreshInterval, setRefreshInterval] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);

  // Assign staff modal state
  const [assignBooking, setAssignBooking] = useState(null);
  // Reschedule modal state
  const [rescheduleBooking, setRescheduleBooking] = useState(null);

  // Alert state for unaccepted confirmed bookings
  const [alertBooking, setAlertBooking] = useState(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const audioRef = useRef(null);
  const alertPollRef = useRef(null);
  const knownAcceptedIds = useRef(new Set());

  const refreshOptions = [
    { label: 'Off', value: 0 },
    { label: '5s', value: 5 },
    { label: '10s', value: 10 },
    { label: '30s', value: 30 },
    { label: '1m', value: 60 },
    { label: '2m', value: 120 },
    { label: '5m', value: 300 },
  ];

  useEffect(() => {
    loadBookings();
  }, [TableDataReducer?.pagination?.page, statusFilter]);

  // Auto-refresh logic
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    if (refreshInterval > 0) {
      setCountdown(refreshInterval);
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => (prev <= 1 ? refreshInterval : prev - 1));
      }, 1000);
      intervalRef.current = setInterval(() => {
        loadBookings();
        setCountdown(refreshInterval);
      }, refreshInterval * 1000);
    } else {
      setCountdown(0);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [refreshInterval, statusFilter]);

  // Poll for unaccepted confirmed bookings every 10s
  useEffect(() => {
    checkUnacceptedBookings();
    alertPollRef.current = setInterval(checkUnacceptedBookings, 10000);
    return () => {
      if (alertPollRef.current) clearInterval(alertPollRef.current);
      stopAlertSound();
    };
  }, []);

  const checkUnacceptedBookings = () => {
    const json = {
      page: 1,
      limit: 1,
      sort: { createdAt: -1 },
      search: { bookingType: 'APP', bookingStatus: 'CONFIRMED', isAccepted: { $ne: true } },
    };
    HitApi(json, searchAppBooking).then((res) => {
      if (res?.statusCode === 200) {
        const docs = Array.isArray(res?.data?.docs) ? res.data.docs : [];
        if (docs.length > 0 && !knownAcceptedIds.current.has(docs[0]._id)) {
          setAlertBooking(docs[0]);
          playAlertSound();
        }
      }
    }).catch(() => {});
  };

  const playAlertSound = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio('/booking-alert.mp3');
      audioRef.current.loop = true;
    }
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  };

  const stopAlertSound = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const handleAcceptBooking = () => {
    if (!alertBooking) return;
    setIsAccepting(true);
    const userData = getElevateUser();
    const json = {
      _id: alertBooking._id,
      acceptedBy: userData?._id || null,
    };
    HitApi(json, acceptBooking).then((res) => {
      if (res?.statusCode === 200) {
        knownAcceptedIds.current.add(alertBooking._id);
        stopAlertSound();
        setAlertBooking(null);
        loadBookings();
      }
      setIsAccepting(false);
    }).catch(() => {
      setIsAccepting(false);
    });
  };

  const loadBookings = (searchQuery = {}) => {
    const query = { ...searchQuery, bookingType: 'APP' };
    if (statusFilter) {
      query.bookingStatus = statusFilter;
    }

    const json = {
      page: TableDataReducer?.pagination?.page || 1,
      limit: TableDataReducer?.pagination?.limit || 15,
      sort: { createdAt: -1 },
      search: query,
    };
    HitApi(json, searchAppBooking).then((res) => {
      if (res?.statusCode === 200) {
        const docs = Array.isArray(res?.data?.docs) ? res.data.docs : [];
        dispatch(setApiJson(docs));
        dispatch(setPagination({
          total: res?.data?.totalDocs || 0,
          page: TableDataReducer?.pagination?.page || 1,
          limit: TableDataReducer?.pagination?.limit || 15,
        }));
      } else {
        dispatch(setApiJson([]));
      }
    }).catch(() => {
      dispatch(setApiJson([]));
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      IN_CART: { bg: 'bg-gray-100', text: 'text-gray-700', icon: <ShoppingCart className="w-3 h-3" />, label: 'In Cart' },
      CONFIRMED: { bg: 'bg-green-100', text: 'text-green-700', icon: <CheckCircle className="w-3 h-3" />, label: 'Confirmed' },
      PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: <Clock className="w-3 h-3" />, label: 'Pending' },
      CANCELLED: { bg: 'bg-red-100', text: 'text-red-700', icon: <XCircle className="w-3 h-3" />, label: 'Cancelled' },
      COMPLETED: { bg: 'bg-blue-100', text: 'text-blue-700', icon: <CheckCircle className="w-3 h-3" />, label: 'Completed' },
      NO_SHOW: { bg: 'bg-orange-100', text: 'text-orange-700', icon: <UserCheck className="w-3 h-3" />, label: 'No Show' },
      RESCHEDULED: { bg: 'bg-purple-100', text: 'text-purple-700', icon: <RotateCcw className="w-3 h-3" />, label: 'Rescheduled' },
      IN_PROGRESS: { bg: 'bg-orange-100', text: 'text-orange-700', icon: <Clock className="w-3 h-3" />, label: 'In Progress' },
    }[status] || { bg: 'bg-gray-100', text: 'text-gray-700', icon: <Clock className="w-3 h-3" />, label: status || '—' };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const getPaymentBadge = (status) => {
    const config = {
      Paid: { bg: 'bg-green-100', text: 'text-green-700' },
      Pending: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      Partial: { bg: 'bg-orange-100', text: 'text-orange-700' },
      Cancelled: { bg: 'bg-red-100', text: 'text-red-700' },
    }[status] || { bg: 'bg-gray-100', text: 'text-gray-700' };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {status || '—'}
      </span>
    );
  };

  const handleSearch = () => {
    if (optionSelect && valueSearched) {
      setIsSearching(true);
      let query = {};
      if (optionSelect === 'client.name' || optionSelect === 'client.phoneNumber') {
        query[optionSelect] = { $regex: valueSearched, $options: 'i' };
      } else {
        query[optionSelect] = { $regex: valueSearched, $options: 'i' };
      }
      dispatch(setPagination({ page: 1 }));
      loadBookings(query);
      setIsSearching(false);
    }
  };

  const handleClearSearch = () => {
    setOptionSelect('');
    setValueSearched('');
    setIsSearching(false);
    dispatch(setPagination({ page: 1 }));
    loadBookings();
  };

  const handleDateSearch = (startDate, endDate) => {
    if (startDate && endDate) {
      const query = {
        createdAt: {
          $gte: new Date(startDate).toISOString(),
          $lte: new Date(new Date(endDate).setHours(23, 59, 59, 999)).toISOString(),
        },
      };
      dispatch(setPagination({ page: 1 }));
      loadBookings(query);
    }
  };

  const baseIndex = ((TableDataReducer?.pagination?.page || 1) - 1) * (TableDataReducer?.pagination?.limit || 15);

  const tableHeaders = [
    {
      title: 'S.No',
      key: 'serialNumber',
      width: '60px',
      align: 'center',
      render: (_, __, index) => (
        <span className="text-sm font-medium text-gray-900">{baseIndex + index + 1}</span>
      ),
    },
    {
      title: 'Booking',
      key: 'bookingNumber',
      render: (value, row) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{value || '—'}</p>
          <p className="text-xs text-gray-400">{row.serviceType === 'athome' ? 'At Home' : 'Salon'}</p>
        </div>
      ),
    },
    {
      title: 'Client',
      key: 'client',
      render: (value) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{value?.name || '—'}</p>
          {value?.phoneNumber && <p className="text-xs text-gray-500">{value.phoneNumber}</p>}
        </div>
      ),
    },
    {
      title: 'Services',
      key: 'services',
      render: (value) => (
        <div className="max-w-[200px]">
          {(value || []).slice(0, 2).map((s, i) => (
            <p key={i} className="text-xs text-gray-700 truncate">{s.name}</p>
          ))}
          {(value || []).length > 2 && (
            <p className="text-xs text-gray-400">+{value.length - 2} more</p>
          )}
          {(!value || value.length === 0) && <span className="text-xs text-gray-400">—</span>}
        </div>
      ),
    },
    {
      title: 'Schedule',
      key: 'bookingDate',
      render: (value, row) => (
        <div>
          <p className="text-xs font-medium text-gray-700">{value || '—'}</p>
          {row.bookingTime && <p className="text-xs text-gray-500">{row.bookingTime}</p>}
        </div>
      ),
    },
    {
      title: 'Amount',
      key: 'calculations',
      render: (value) => (
        <span className="text-sm font-semibold text-gray-900">
          ₹{value?.totals?.grandTotal?.toFixed(2) || '0.00'}
        </span>
      ),
    },
    {
      title: 'Status',
      key: 'bookingStatus',
      render: (value) => getStatusBadge(value),
    },
    {
      title: 'Payment',
      key: 'payment',
      render: (value) => getPaymentBadge(value?.paymentStatus),
    },
    {
      title: 'Accepted',
      key: 'isAccepted',
      width: '80px',
      render: (value) => value ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3" /> Yes
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">
          <Clock className="w-3 h-3" /> No
        </span>
      ),
    },
    {
      title: 'Date',
      key: 'createdAt',
      render: (value) => (
        <span className="text-xs text-gray-500">
          {value ? new Date(value).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric'
          }) : '—'}
        </span>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: '130px',
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => { setSelectedBooking(row); setIsModalOpen(true); }}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => setAssignBooking(row)}
            className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
            title="Assign Staff"
          >
            <Users className="w-4 h-4" />
          </button>
          {row.bookingStatus !== 'CANCELLED' && row.bookingStatus !== 'COMPLETED' && row.bookingStatus !== 'RESCHEDULED' && (
            <button
              onClick={() => setRescheduleBooking(row)}
              className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
              title="Reschedule"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const options = [
    { label: 'Booking No', value: 'bookingNumber' },
    { label: 'Client Name', value: 'client.name' },
    { label: 'Phone', value: 'client.phoneNumber' },
  ];

  const statusFilters = [
    { label: 'All', value: '' },
    { label: 'In Cart', value: 'IN_CART' },
    { label: 'Confirmed', value: 'CONFIRMED' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Cancelled', value: 'CANCELLED' },
    { label: 'No Show', value: 'NO_SHOW' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Rescheduled', value: 'RESCHEDULED' },
  ];

  return (
    <div className="p-4">
      <PageHeader title="App Bookings" />

      {/* Status Filter Chips + Auto Refresh */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-2">
          {statusFilters.map((f) => (
            <button
              key={f.value}
              onClick={() => { setStatusFilter(f.value); dispatch(setPagination({ page: 1 })); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                statusFilter === f.value
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => loadBookings()}
            className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh now"
          >
            <RefreshCw className={`w-4 h-4 ${refreshInterval > 0 ? 'animate-spin-slow' : ''}`} />
          </button>
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            {refreshOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setRefreshInterval(opt.value)}
                className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                  refreshInterval === opt.value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {refreshInterval > 0 && (
            <span className="text-xs text-gray-400 min-w-[28px] text-center">{countdown}s</span>
          )}
        </div>
      </div>

      <AppTable
        TH={tableHeaders}
        TD={Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : []}
        isLoading={!Array.isArray(ApiReducer.apiJson) || ApiReducer.apiJson.length === 0 || isSearching}
        emptyMessage="No bookings found"
        TableSearch={
          <TableSearch
            options={options}
            onClick={handleSearch}
            optionSelect={optionSelect}
            setOptionSelect={setOptionSelect}
            setValueSearched={setValueSearched}
            valueSearched={valueSearched}
            onClear={handleClearSearch}
          >
            <DateSearch onSearch={handleDateSearch} />
          </TableSearch>
        }
      />

      {/* New Booking Alert Popup */}
      {alertBooking && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-bounce-in">
            {/* Pulsing header */}
            <div className="bg-green-500 p-4 flex items-center gap-3 relative overflow-hidden">
              <div className="absolute inset-0 bg-green-400 animate-pulse opacity-50" />
              <div className="relative z-10 flex items-center gap-3 w-full">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Bell className="w-6 h-6 text-white animate-bounce" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-bold text-lg">New Booking!</h3>
                  <p className="text-green-100 text-sm">{alertBooking.bookingNumber}</p>
                </div>
                <Volume2 className="w-5 h-5 text-white animate-pulse" />
              </div>
            </div>

            <div className="p-4 space-y-3">
              {/* Client */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-700 font-bold text-lg">
                  {alertBooking.client?.name?.[0]?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{alertBooking.client?.name || 'Unknown Client'}</p>
                  {alertBooking.client?.phoneNumber && (
                    <p className="text-xs text-gray-500">{alertBooking.client.phoneNumber}</p>
                  )}
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Type</p>
                  <p className="text-sm font-medium">{alertBooking.serviceType === 'athome' ? 'At Home' : 'Salon'}</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Amount</p>
                  <p className="text-sm font-bold text-green-700">₹{alertBooking.calculations?.totals?.grandTotal?.toFixed(2) || '0.00'}</p>
                </div>
                {alertBooking.bookingDate && (
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Date</p>
                    <p className="text-sm font-medium">{alertBooking.bookingDate}</p>
                  </div>
                )}
                {alertBooking.bookingTime && (
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Time</p>
                    <p className="text-sm font-medium">{alertBooking.bookingTime}</p>
                  </div>
                )}
              </div>

              {/* Services */}
              {alertBooking.services?.length > 0 && (
                <div className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Services</p>
                  {alertBooking.services.map((s, i) => (
                    <p key={i} className="text-xs text-gray-700">{s.name}{s.quantity > 1 ? ` x${s.quantity}` : ''}</p>
                  ))}
                </div>
              )}

              {/* Address for at-home */}
              {alertBooking.address?.fullAddress && (
                <div className="p-2 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-1 mb-1">
                    <MapPin className="w-3 h-3 text-orange-500" />
                    <p className="text-[10px] text-orange-500 uppercase tracking-wide">Address</p>
                  </div>
                  <p className="text-xs text-orange-800">{alertBooking.address.fullAddress}</p>
                </div>
              )}

              {/* Accept Button */}
              <button
                onClick={handleAcceptBooking}
                disabled={isAccepting}
                className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-bold text-sm rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                {isAccepting ? 'Accepting...' : 'Accept Booking'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Staff Modal */}
      {assignBooking && (
        <AssignStaffModal
          booking={assignBooking}
          onClose={() => setAssignBooking(null)}
          onSaved={() => {
            setAssignBooking(null);
            loadBookings();
          }}
        />
      )}

      {/* Reschedule Modal */}
      {rescheduleBooking && (
        <RescheduleModal
          booking={rescheduleBooking}
          onClose={() => setRescheduleBooking(null)}
          onSaved={() => {
            setRescheduleBooking(null);
            loadBookings();
          }}
        />
      )}

      {/* Booking Detail Modal */}
      <CustomModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} width="max-w-2xl">
        {selectedBooking && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selectedBooking.bookingNumber}</h3>
                <p className="text-xs text-gray-500">
                  {selectedBooking.serviceType === 'athome' ? 'At Home Service' : 'Salon Service'}
                </p>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              {/* Status + Payment */}
              <div className="flex items-center gap-3">
                {getStatusBadge(selectedBooking.bookingStatus)}
                {getPaymentBadge(selectedBooking.payment?.paymentStatus)}
                {selectedBooking.paymentDone && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Payment Done</span>
                )}
              </div>

              {/* Client */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Client</p>
                <p className="text-sm font-medium">{selectedBooking.client?.name || '—'}</p>
                {selectedBooking.client?.phoneNumber && (
                  <p className="text-xs text-gray-400">{selectedBooking.client.phoneNumber}</p>
                )}
              </div>

              {/* Schedule */}
              {(selectedBooking.bookingDate || selectedBooking.bookingTime) && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-3 h-3 text-blue-600" />
                    <p className="text-xs text-blue-600 font-medium">Schedule</p>
                  </div>
                  <p className="text-sm font-medium text-blue-900">
                    {selectedBooking.bookingDate} {selectedBooking.bookingTime && `• ${selectedBooking.bookingTime}`}
                  </p>
                </div>
              )}

              {/* Address (for at-home) */}
              {selectedBooking.address?.fullAddress && (
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-3 h-3 text-orange-600" />
                    <p className="text-xs text-orange-600 font-medium">Delivery Address</p>
                  </div>
                  <p className="text-sm text-orange-900">{selectedBooking.address.fullAddress}</p>
                  {selectedBooking.address.landmark && (
                    <p className="text-xs text-orange-500 mt-1">Landmark: {selectedBooking.address.landmark}</p>
                  )}
                </div>
              )}

              {/* Services */}
              {selectedBooking.services?.length > 0 && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Services</p>
                  <div className="space-y-2">
                    {selectedBooking.services.map((s, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {s.img && <img src={s.img} alt="" className="w-8 h-8 rounded object-cover" />}
                          <div>
                            <p className="text-sm font-medium">{s.name}</p>
                            {s.quantity > 1 && <p className="text-xs text-gray-400">x{s.quantity}</p>}
                          </div>
                        </div>
                        <span className="text-sm font-semibold">₹{s.pricing?.finalPrice || s.pricing?.basePrice || 0}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Products */}
              {selectedBooking.products?.length > 0 && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Products</p>
                  <div className="space-y-2">
                    {selectedBooking.products.map((p, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">{p.name}</p>
                          {p.brand && <p className="text-xs text-gray-400">{p.brand}</p>}
                        </div>
                        <span className="text-sm font-semibold">₹{p.pricing?.finalPrice || p.pricing?.basePrice || 0}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment Summary */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-2">Payment</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-400">Grand Total:</span>{' '}
                    <span className="font-bold text-sm">₹{selectedBooking.calculations?.totals?.grandTotal?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Total Paid:</span>{' '}
                    <span className="font-bold text-sm">₹{selectedBooking.payment?.totalPaid?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
                {selectedBooking.payment?.activePaymentMethods?.length > 0 && (
                  <div className="flex gap-2 mt-2">
                    {selectedBooking.payment.activePaymentMethods.map((m, i) => (
                      <span key={i} className="px-2 py-0.5 rounded bg-gray-200 text-xs font-medium">
                        {m.method}: ₹{m.amount}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Cancellation */}
              {selectedBooking.cancellationReason && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-xs text-red-500">Cancellation Reason</p>
                  <p className="text-sm text-red-700">{selectedBooking.cancellationReason}</p>
                  {selectedBooking.cancelledAt && (
                    <p className="text-xs text-red-400 mt-1">
                      {new Date(selectedBooking.cancelledAt).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  )}
                </div>
              )}

              {/* Timestamps */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Created</p>
                <p className="text-sm">
                  {selectedBooking.createdAt
                    ? new Date(selectedBooking.createdAt).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })
                    : '—'}
                </p>
              </div>
            </div>
          </div>
        )}
      </CustomModal>
    </div>
  );
}

export default AppBookingMaster;
